import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TournamentJoinAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trophy, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Public landing page hit when a club scans the organizer's QR / opens the invite link.
 * No auth required. Adds the visiting club's team to the tournament standings.
 */
export default function TournamentJoin() {
  const { code } = useParams();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    team_name: "",
    team_logo_url: "",
    contact_email: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    TournamentJoinAPI.preview(code)
      .then((data) => {
        if (alive) setPreview(data);
      })
      .catch((err) => {
        if (alive)
          setError(
            err?.response?.data?.detail ||
              "Lien d'invitation invalide ou désactivé.",
          );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.team_name.trim()) {
      toast.error("Nom de l'équipe obligatoire");
      return;
    }
    setSaving(true);
    try {
      const updated = await TournamentJoinAPI.submit(code, {
        team_name: form.team_name.trim(),
        team_logo_url: form.team_logo_url.trim(),
        contact_email: form.contact_email.trim(),
      });
      setPreview(updated);
      setSubmitted(true);
      toast.success("Inscription confirmée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center font-body text-zinc-500"
        data-testid="tournament-join-loading"
      >
        Chargement…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6"
        data-testid="tournament-join-error"
      >
        <div className="max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/30 mb-5">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-2">
            Lien indisponible
          </h1>
          <p className="text-zinc-400 font-body">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero */}
      <div
        className="relative h-56 sm:h-64 overflow-hidden"
        style={{
          background: preview.cover_image_url
            ? `linear-gradient(180deg, rgba(5,5,5,0.4), rgba(5,5,5,0.95)), url(${preview.cover_image_url}) center/cover`
            : `linear-gradient(180deg, ${preview.primary_color}33, #050505)`,
        }}
      >
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-2xl mx-auto px-6 pb-6 w-full">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2 inline-flex items-center gap-2">
              <Trophy className="w-3 h-3" /> Invitation Tournoi · Laser Tech
              Création
            </div>
            <h1
              className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight"
              data-testid="tournament-join-name"
            >
              {preview.name}
            </h1>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mt-1">
              {[preview.sport, preview.season].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="metal-card rounded-xl p-8 text-center"
            data-testid="tournament-join-success"
          >
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-green-500/10 border border-green-500/30 mb-5">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-2">
              Inscription confirmée
            </h2>
            <p className="text-zinc-400 font-body text-sm mb-6">
              Ton équipe <b className="text-white">{form.team_name}</b> est
              maintenant inscrite à <b className="text-white">{preview.name}</b>
              . L'organisateur va saisir les résultats au fil des matchs.
            </p>
            <p className="text-zinc-500 font-body text-xs">
              {preview.teams_count} équipe
              {preview.teams_count > 1 ? "s" : ""} participent à ce tournoi.
            </p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="metal-card rounded-xl p-6"
            data-testid="tournament-join-form"
          >
            {preview.subtitle && (
              <p className="text-zinc-300 font-body mb-5">
                {preview.subtitle}
              </p>
            )}
            <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-1">
              Inscris ton équipe
            </h2>
            <p className="text-zinc-400 font-body text-sm mb-6">
              {preview.teams_count} équipe
              {preview.teams_count > 1 ? "s" : ""} déjà inscrite
              {preview.teams_count > 1 ? "s" : ""}. Ajoute la tienne ci-dessous.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-overline">Nom de l'équipe *</Label>
                <Input
                  value={form.team_name}
                  onChange={(e) =>
                    setForm({ ...form, team_name: e.target.value })
                  }
                  placeholder="Ex : FC Lausanne U17"
                  className="bg-white/[0.04] border-white/10 text-white"
                  data-testid="join-team-name"
                  required
                />
              </div>
              <div>
                <Label className="text-overline">Logo (URL)</Label>
                <Input
                  value={form.team_logo_url}
                  onChange={(e) =>
                    setForm({ ...form, team_logo_url: e.target.value })
                  }
                  placeholder="https://…"
                  className="bg-white/[0.04] border-white/10 text-white"
                  data-testid="join-team-logo"
                />
              </div>
              <div>
                <Label className="text-overline">Email de contact</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) =>
                    setForm({ ...form, contact_email: e.target.value })
                  }
                  placeholder="contact@club.ch"
                  className="bg-white/[0.04] border-white/10 text-white"
                  data-testid="join-contact-email"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              data-testid="join-submit"
              className="text-black font-bold uppercase tracking-[0.18em] text-xs h-11 px-7 w-full sm:w-auto"
              style={{
                background:
                  "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
              }}
            >
              {saving ? "Inscription…" : "Rejoindre le tournoi"}
            </Button>

            {preview.organizer_email && (
              <p className="text-[11px] text-zinc-500 font-body mt-5">
                Tournoi organisé par{" "}
                <span className="text-zinc-300">
                  {preview.organizer_email}
                </span>
              </p>
            )}
          </motion.form>
        )}
      </div>
    </div>
  );
}
